import { useState } from 'react'
import { aiApiService, type FieldMapping, type GenerateTemplateResponse, type InferredVariable, type InferSchemaResponse, type StarterTemplateResponse, type SuggestedVariable, type VariableHint } from '../services/aiApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'

type Tab = 'starters' | 'generate' | 'map' | 'schema'

export default function AiPage() {
  const [tab, setTab] = useState<Tab>('starters')

  return (
    <div>
      <div style={{ marginBottom: spacing.xl }}>
        <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0 }}>
          AI Assistant
        </h1>
        <p style={{ color: colors.textSecondary, marginTop: spacing.xs, fontSize: fontSize.base }}>
          Generate templates from a description, or map JSON fields to template variables.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, borderBottom: `1px solid ${colors.border}` }}>
        {([['starters', 'Starter Templates'], ['generate', 'Generate Template'], ['map', 'Map Fields'], ['schema', 'Infer Schema']] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: 'none',
              border: 'none',
              borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent',
              fontSize: fontSize.base,
              fontWeight: tab === t ? 600 : 400,
              color: tab === t ? colors.primary : colors.textSecondary,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'starters' ? <StartersTab /> : tab === 'generate' ? <GenerateTab /> : tab === 'map' ? <MapTab /> : <InferSchemaTab />}
    </div>
  )
}

const STARTER_TYPES = [
  { key: 'invoice',     label: 'Invoice',     description: 'Professional invoice with line items, taxes, and payment terms.' },
  { key: 'contract',    label: 'Contract',    description: 'Business contract with parties, terms, and signature blocks.' },
  { key: 'certificate', label: 'Certificate', description: 'Achievement or completion certificate with recipient details.' },
  { key: 'statement',   label: 'Statement',   description: 'Financial account statement summarizing activity and balances.' },
  { key: 'report',      label: 'Report',      description: 'Structured report with executive summary, data sections, and findings.' },
]

function TemplateResult({ result }: { result: StarterTemplateResponse | GenerateTemplateResponse }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    await navigator.clipboard.writeText(result.templateText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      <div style={{
        background: colors.surface, border: `1px solid ${colors.border}`,
        borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.lg,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm }}>
          <div style={{ fontWeight: 600, color: colors.text, fontSize: fontSize.base }}>Template</div>
          <button
            onClick={handleCopy}
            style={{
              padding: '4px 12px', background: 'none', border: `1px solid ${colors.border}`,
              borderRadius: radius.sm, fontSize: fontSize.sm, cursor: 'pointer', color: colors.text,
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre style={{
          margin: 0, padding: spacing.md, background: '#0A1413',
          borderRadius: radius.sm, border: `1px solid ${colors.border}`,
          fontSize: fontSize.sm, fontFamily: fontFamily.mono,
          lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          maxHeight: 400, color: '#e2e8f0',
        }}>
          {result.templateText}
        </pre>
      </div>
      {result.variables.length > 0 && (
        <div style={{
          background: colors.surface, border: `1px solid ${colors.border}`,
          borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.lg,
        }}>
          <div style={{ fontWeight: 600, color: colors.text, fontSize: fontSize.base, marginBottom: spacing.sm }}>
            Variables ({result.variables.length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
            <thead>
              <tr>
                {['Variable', 'Kind', 'Description', 'Example'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.variables.map((v, i) => (
                <tr key={i}>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}`, fontFamily: fontFamily.mono, color: colors.primary }}>{`{{${v.name}}}`}</td>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary }}>{v.kind}</td>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}`, color: colors.text }}>{v.description}</td>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${colors.border}`, color: colors.textMuted, fontFamily: fontFamily.mono }}>{v.exampleValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function StartersTab() {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [result, setResult] = useState<StarterTemplateResponse | null>(null)

  async function handleGenerate(key: string) {
    setLoading(key); setError(''); setResult(null)
    try {
      const res = await aiApiService.starterTemplate(key)
      setResult(res)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Generation failed')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: spacing.md,
      }}>
        {STARTER_TYPES.map(t => (
          <button
            key={t.key}
            onClick={() => handleGenerate(t.key)}
            disabled={loading !== null}
            style={{
              padding: spacing.md,
              background: result?.docType === t.key ? colors.primaryLight : colors.surface,
              border: `1px solid ${result?.docType === t.key ? colors.primary : colors.border}`,
              borderRadius: radius.md,
              boxShadow: shadow.xs,
              cursor: loading !== null ? 'default' : 'pointer',
              textAlign: 'left',
              opacity: loading !== null && loading !== t.key ? 0.5 : 1,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: fontSize.base, color: colors.text, marginBottom: 4 }}>
              {loading === t.key ? 'Generating…' : t.label}
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{t.description}</div>
          </button>
        ))}
      </div>
      {error && <div style={{ fontSize: fontSize.sm, color: colors.error }}>{error}</div>}
      {result && <TemplateResult result={result} />}
    </div>
  )
}

function GenerateTab() {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<GenerateTemplateResponse | null>(null)
  const [copied, setCopied] = useState(false)

  async function handleGenerate() {
    if (!prompt.trim()) return
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const res = await aiApiService.generateTemplate(prompt.trim())
      setResult(res)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'AI generation failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleCopy() {
    if (!result) return
    await navigator.clipboard.writeText(result.templateText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const examplePrompts = [
    'Invoice template with line items, taxes, and totals',
    'Employee offer letter with compensation details and start date',
    'Sales proposal for software services with pricing tiers',
    'Rental agreement with tenant details and monthly rent',
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        boxShadow: shadow.xs,
        padding: spacing.lg,
      }}>
        <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>
          Describe your document
        </div>
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder="e.g. An invoice for a consulting company with client details, itemized services, and payment terms"
          rows={4}
          style={{
            width: '100%',
            padding: spacing.sm,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            fontSize: fontSize.base,
            color: colors.text,
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.sm, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            style={{
              padding: '8px 20px',
              background: loading || !prompt.trim() ? colors.textMuted : colors.primary,
              color: colors.onPrimary,
              border: 'none',
              borderRadius: radius.sm,
              fontSize: fontSize.base,
              fontWeight: 600,
              cursor: loading || !prompt.trim() ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Generating…' : 'Generate'}
          </button>
          <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>
            Powered by Claude {prompt.length}/2000
          </span>
        </div>

        {!result && !loading && (
          <div style={{ marginTop: spacing.md }}>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs }}>
              Try an example:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
              {examplePrompts.map(ex => (
                <button
                  key={ex}
                  onClick={() => setPrompt(ex)}
                  style={{
                    padding: '4px 10px',
                    background: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: radius.sm,
                    fontSize: fontSize.xs,
                    color: colors.textSecondary,
                    cursor: 'pointer',
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ color: colors.error, fontSize: fontSize.sm, padding: spacing.sm, background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm }}>
          {error}
        </div>
      )}

      {result && (
        <>
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.xs,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: colors.background,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary }}>
                Generated Template
              </span>
              <button
                onClick={handleCopy}
                style={{
                  padding: '4px 12px',
                  background: colors.primary,
                  color: colors.onPrimary,
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.xs,
                  cursor: 'pointer',
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre style={{
              margin: 0,
              padding: spacing.md,
              fontFamily: fontFamily.mono,
              fontSize: fontSize.sm,
              color: '#e2e8f0',
              background: '#0A1413',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 400,
              overflowY: 'auto',
            }}>
              {result.templateText}
            </pre>
          </div>

          {result.variables.length > 0 && (
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              boxShadow: shadow.xs,
              overflow: 'hidden',
            }}>
              <div style={{
                padding: `${spacing.sm} ${spacing.md}`,
                background: colors.background,
                borderBottom: `1px solid ${colors.border}`,
                fontSize: fontSize.sm,
                fontWeight: 600,
                color: colors.textSecondary,
              }}>
                Variables ({result.variables.length})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {(['Variable', 'Kind', 'Description', 'Example'] as const).map(h => (
                      <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, background: colors.background }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.variables.map((v: SuggestedVariable, i: number) => (
                    <tr key={i}>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: fontFamily.mono, fontSize: fontSize.sm, borderBottom: `1px solid ${colors.border}` }}>
                        {`{{${v.name}}}`}
                      </td>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.xs, borderBottom: `1px solid ${colors.border}` }}>
                        <span style={{ color: colors.textSecondary, background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '1px 6px' }}>
                          {v.kind}
                        </span>
                      </td>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text, borderBottom: `1px solid ${colors.border}` }}>
                        {v.description}
                      </td>
                      <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
                        {v.exampleValue}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function MapTab() {
  const [payloadJson, setPayloadJson] = useState('{\n  "customer": {\n    "name": "Acme Corp",\n    "email": "billing@acme.com"\n  },\n  "invoiceNumber": "INV-2024-001",\n  "totalAmount": 1500.00\n}')
  const [variableKeys, setVariableKeys] = useState('customerName\ncustomerEmail\ninvoiceNumber\ntotal')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [mappings, setMappings] = useState<FieldMapping[] | null>(null)

  async function handleMap() {
    setLoading(true)
    setError('')
    setMappings(null)
    try {
      const keys = variableKeys.split('\n').map(k => k.trim()).filter(Boolean)
      const res = await aiApiService.mapFields(payloadJson, keys)
      setMappings(res.mappings)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Field mapping failed')
    } finally {
      setLoading(false)
    }
  }

  function confidenceColor(c: number) {
    if (c >= 0.9) return colors.success
    if (c >= 0.7) return colors.warning
    return colors.error
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.md }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
            JSON Payload
          </div>
          <textarea
            value={payloadJson}
            onChange={e => setPayloadJson(e.target.value)}
            rows={12}
            style={{
              width: '100%',
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontFamily: fontFamily.mono,
              fontSize: fontSize.sm,
              color: colors.text,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.md }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
            Template Variables (one per line)
          </div>
          <textarea
            value={variableKeys}
            onChange={e => setVariableKeys(e.target.value)}
            rows={12}
            placeholder="customerName&#10;invoiceNumber&#10;totalAmount"
            style={{
              width: '100%',
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontFamily: fontFamily.mono,
              fontSize: fontSize.sm,
              color: colors.text,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      <div>
        <button
          onClick={handleMap}
          disabled={loading || !payloadJson.trim() || !variableKeys.trim()}
          style={{
            padding: '8px 20px',
            background: loading ? colors.textMuted : colors.primary,
            color: colors.onPrimary,
            border: 'none',
            borderRadius: radius.sm,
            fontSize: fontSize.base,
            fontWeight: 600,
            cursor: loading ? 'default' : 'pointer',
          }}
        >
          {loading ? 'Mapping…' : 'Map Fields'}
        </button>
        <span style={{ marginLeft: spacing.sm, fontSize: fontSize.xs, color: colors.textMuted }}>
          Powered by Claude
        </span>
      </div>

      {error && (
        <div style={{ color: colors.error, fontSize: fontSize.sm, padding: spacing.sm, background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm }}>
          {error}
        </div>
      )}

      {mappings && (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, overflow: 'hidden' }}>
          <div style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.background, borderBottom: `1px solid ${colors.border}`, fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary }}>
            Suggested Mappings ({mappings.length})
          </div>
          {mappings.length === 0 ? (
            <div style={{ padding: spacing.md, color: colors.textSecondary, fontSize: fontSize.sm }}>
              No confident mappings found. Try more descriptive variable names.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {(['Template Variable', 'JSON Path', 'Confidence', 'Reasoning'] as const).map(h => (
                    <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, background: colors.background }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mappings.map((m: FieldMapping, i: number) => (
                  <tr key={i}>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: fontFamily.mono, fontSize: fontSize.sm, borderBottom: `1px solid ${colors.border}` }}>
                      {`{{${m.variableKey}}}`}
                    </td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.primary, borderBottom: `1px solid ${colors.border}` }}>
                      {m.jsonPath}
                    </td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, borderBottom: `1px solid ${colors.border}` }}>
                      <span style={{ fontSize: fontSize.xs, fontWeight: 600, color: confidenceColor(m.confidence) }}>
                        {Math.round(m.confidence * 100)}%
                      </span>
                    </td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.xs, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                      {m.reasoning}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

function InferSchemaTab() {
  const [mode, setMode] = useState<'manual' | 'version'>('manual')
  const [templateVersionId, setTemplateVersionId] = useState('')
  const [variableText, setVariableText] = useState(
    'customerName scalar\ncustomerEmail scalar\norderNumber scalar\nitems loop\nitems.name scalar\nitems.price scalar\nisPremium conditional\ntotal scalar'
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<InferSchemaResponse | null>(null)
  const [schemaTab, setSchemaTab] = useState<'variables' | 'schema' | 'sample'>('variables')

  function parseVariableText(): VariableHint[] {
    return variableText.split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const parts = line.split(/\s+/)
        const name = parts[0]
        const kind = (['scalar', 'loop', 'conditional'].includes(parts[1] ?? ''))
          ? (parts[1] as 'scalar' | 'loop' | 'conditional')
          : 'scalar'
        return { name, kind }
      })
  }

  async function handleInfer() {
    setLoading(true); setError(''); setResult(null)
    try {
      const res = mode === 'version'
        ? await aiApiService.inferSchema(templateVersionId.trim())
        : await aiApiService.inferSchema(parseVariableText())
      setResult(res)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Schema inference failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleCopy(text: string) {
    await navigator.clipboard.writeText(text).catch(() => null)
  }

  const typeColor = (t: string) => {
    switch (t) {
      case 'string':  return colors.primary
      case 'number':  return '#059669'
      case 'boolean': return '#d97706'
      case 'array':   return '#7c3aed'
      case 'object':  return '#0891b2'
      default:        return colors.text
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.lg }}>
        <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.md }}>
          {(['manual', 'version'] as const).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: '6px 14px',
                background: mode === m ? colors.primaryLight : colors.background,
                border: `1px solid ${mode === m ? colors.primary : colors.border}`,
                borderRadius: radius.sm,
                color: mode === m ? colors.primary : colors.text,
                fontSize: fontSize.sm,
                fontWeight: mode === m ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {m === 'manual' ? 'Enter variables' : 'Template version ID'}
            </button>
          ))}
        </div>

        {mode === 'manual' ? (
          <>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs }}>
              One variable per line: <code>name kind</code> — kind is <code>scalar</code>, <code>loop</code>, or <code>conditional</code> (defaults to scalar)
            </div>
            <textarea
              value={variableText}
              onChange={e => { setVariableText(e.target.value); setResult(null) }}
              rows={10}
              style={{
                width: '100%',
                padding: spacing.sm,
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontFamily: fontFamily.mono,
                fontSize: fontSize.sm,
                color: colors.text,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </>
        ) : (
          <input
            type="text"
            value={templateVersionId}
            onChange={e => { setTemplateVersionId(e.target.value); setResult(null) }}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            style={{
              width: '100%',
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: fontSize.base,
              color: colors.text,
              boxSizing: 'border-box',
            }}
          />
        )}

        <div style={{ marginTop: spacing.md, display: 'flex', alignItems: 'center', gap: spacing.md }}>
          <button
            onClick={handleInfer}
            disabled={loading || (mode === 'manual' ? !variableText.trim() : !templateVersionId.trim())}
            style={{
              padding: '8px 20px',
              background: loading ? colors.textMuted : colors.primary,
              color: colors.onPrimary,
              border: 'none',
              borderRadius: radius.sm,
              fontSize: fontSize.base,
              fontWeight: 600,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Inferring…' : 'Infer Schema'}
          </button>
          <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>Powered by Claude</span>
        </div>
      </div>

      {error && (
        <div style={{ color: colors.error, fontSize: fontSize.sm, padding: spacing.sm, background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, background: colors.background }}>
            {([['variables', `Variables (${result.variables.length})`], ['schema', 'JSON Schema'], ['sample', 'Sample Payload']] as [typeof schemaTab, string][]).map(([t, label]) => (
              <button
                key={t}
                onClick={() => setSchemaTab(t)}
                style={{
                  padding: `${spacing.sm} ${spacing.md}`,
                  background: 'none',
                  border: 'none',
                  borderBottom: schemaTab === t ? `2px solid ${colors.primary}` : '2px solid transparent',
                  fontSize: fontSize.sm,
                  fontWeight: schemaTab === t ? 600 : 400,
                  color: schemaTab === t ? colors.primary : colors.textSecondary,
                  cursor: 'pointer',
                  marginBottom: -1,
                }}
              >
                {label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            {schemaTab !== 'variables' && (
              <button
                onClick={() => handleCopy(schemaTab === 'schema' ? result.jsonSchema : result.samplePayload)}
                style={{ padding: '4px 12px', background: 'none', border: 'none', fontSize: fontSize.xs, color: colors.textSecondary, cursor: 'pointer', margin: spacing.xs }}
              >
                Copy
              </button>
            )}
          </div>

          {schemaTab === 'variables' ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {(['Variable', 'Type', 'Required', 'Description'] as const).map(h => (
                    <th key={h} style={{ padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, background: colors.background }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.variables.map((v: InferredVariable, i: number) => (
                  <tr key={i}>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontFamily: fontFamily.mono, fontSize: fontSize.sm, borderBottom: `1px solid ${colors.border}` }}>
                      {v.name}
                    </td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, borderBottom: `1px solid ${colors.border}` }}>
                      <span style={{ fontSize: fontSize.xs, fontWeight: 600, color: typeColor(v.type), background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '1px 6px' }}>
                        {v.type}
                      </span>
                    </td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.xs, color: v.required ? colors.success : colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
                      {v.required ? 'required' : 'optional'}
                    </td>
                    <td style={{ padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                      {v.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <pre style={{
              margin: 0,
              padding: spacing.md,
              fontFamily: fontFamily.mono,
              fontSize: fontSize.sm,
              color: '#e2e8f0',
              background: '#0A1413',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 480,
              overflowY: 'auto',
            }}>
              {schemaTab === 'schema' ? result.jsonSchema : result.samplePayload}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
